import React from "react";
// Monthly Consumption Report

// Default
import { useState, useEffect } from "react";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import MonthlyConsumptionReportLineChart from "../charts/MonthlyConsumptionReportLineChart";
import CustomDataTable from "../table/CustomTable";

// Utils
import { fetchGETRequest } from "../../utils/services";
import { formatDate } from "../../utils/helper";

// Types
interface SelectOption {
  value: string;
  label: string;
}

interface ConsumptionData {
  cano: string;
  meterno: string;
  date: string;
  kwhconsumption: number;
  kvahconsumption: number;
  kvarhconsumption: number;
  maxdemandvalue: number;
  powerfactor: number;
  [key: string]: any;
}

const MonthlyConsumptionReport: React.FC = () => {
  const months: SelectOption[] = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const years: SelectOption[] = [
    { value: "2022", label: "2022" },
    { value: "2023", label: "2023" },
    { value: "2024", label: "2024" },
  ];

  const [filteredData, setFilteredData] = useState<ConsumptionData[]>([]);
  const [originalData, setOriginalData] = useState<ConsumptionData[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<SelectOption | null>(null);
  const [selectedYear, setSelectedYear] = useState<SelectOption | null>(null);

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchGETRequest(
          `plugins/telemetry/DEVICE/30d33fa0-bb60-11ef-85d5-e5b1b2e5df91/values/timeseries?keys=IP_KWH,EventCode,EventDescription&startTs=1730485800000&endTs=1736015340000&intervalType=WEEK&agg=MIN`,
          {},
          true
        );

        if (
          response?.data &&
          response?.data["IP_KWH"] &&
          response?.data["IP_Current"]
        ) {
          const transformedData = response.data.IP_KWH.map(
            (item: any, index: number) => {
              const maxDemandTimestamp =
                (response.data as any).IP_Current?.[index]?.ts || "";
              const maxDemandDate = maxDemandTimestamp
                ? formatDate(
                    new Date(maxDemandTimestamp),
                    "DD/MM/YYYY HH:mm:ss"
                  )
                : "";
              return {
                cano: "123456789",
                meterno: "987654321",
                date: maxDemandDate,
                kwhconsumption: item.value || 0,
                kvahconsumption: response.data.IP_Current[index]?.value || 0,
                kvarhconsumption: response.data.IP_Current[index]?.value || 0,
                maxdemandvalue: response.data.IP_Current[index]?.value || 0,
                powerfactor: response.data.IP_Current[index]?.value || 0,
              };
            }
          );

          setOriginalData(transformedData);
          setFilteredData(transformedData);
        } else {
          console.error("Unexpected data format:", response.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const COLUMNS = [
    "Consumer Account Number",
    "Meter Number",
    "Date & Month",
    "KWH Consumption",
    "KVAH Consumption",
    "KVARH Consumption",
    "Max Demand Value",
    "Power Factor",
  ];

  const downloadCSV = () => {
    if (filteredData.length === 0) return;

    const headers = Object.keys(filteredData[0]).join(",");
    const rows = filteredData
      .map((row) => Object.values(row).join(","))
      .join("\n");
    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${window.location.pathname.slice(1)}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const filterData = () => {
    if (!selectedMonth && !selectedYear) {
      setFilteredData(originalData);
      return;
    }

    const filtered = originalData.filter((item) => {
      const itemDate = new Date(item.date);
      const itemMonth = (itemDate.getMonth() + 1).toString().padStart(2, "0");
      const itemYear = itemDate.getFullYear().toString();

      return (
        (!selectedMonth || itemMonth === selectedMonth.value) &&
        (!selectedYear || itemYear === selectedYear.value)
      );
    });

    setFilteredData(filtered);
  };

  useEffect(() => {
    filterData();
  }, [selectedMonth, selectedYear]);

  const rowsContent = filteredData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">{item.cano}</Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.meterno}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.date}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.kwhconsumption}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.kvahconsumption}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.kvarhconsumption}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.maxdemandvalue}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.powerfactor}
        </Typography>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Card className="meters_title_card">
        <Typography variant="h5">Monthly Consumption Report</Typography>
      </Card>

      <Card sx={{ mt: "20px", maxHeight: "400px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">
                Monthly Consumption Data Report
              </Typography>
              <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
                <CustomOutlinedSelect
                  placeholder={"Select Month"}
                  options={months}
                  width="fit-content"
                  onChange={(newValue) =>
                    setSelectedMonth(newValue as SelectOption)
                  }
                  value={selectedMonth}
                />
                <CustomOutlinedSelect
                  placeholder={"Select Year"}
                  options={years}
                  width="fit-content"
                  onChange={(newValue) =>
                    setSelectedYear(newValue as SelectOption)
                  }
                  value={selectedYear}
                />
              </Stack>
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          {filteredData?.length === 0 && (
            <Stack
              direction={"row"}
              justifyContent={"center"}
              padding={"10px 0"}
            >
              <Typography variant="body1">
                There are no records to display
              </Typography>
            </Stack>
          )}
          {filteredData?.length > 0 && (
            <MonthlyConsumptionReportLineChart filteredData={filteredData} />
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Report Details</Typography>
              <ContainedButton
                label={"Download CSV"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={downloadCSV}
                disabled={filteredData?.length === 0}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent sx={{ overflow: "auto", maxHeight: "600px" }}>
          <CustomDataTable
            columns={COLUMNS}
            pagination={true}
            rowsContent={rowsContent}
            Actions={true}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={filteredData?.length}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default MonthlyConsumptionReport;
